const deserializeRegex = /^([^,]+),([^,{]+)({([^}]+)})?$/;

export const serializedPositionOffsetDelimiter = ':';
export const serializedPositionPathSegmentDelimiter = '/';

export function convertStringIndexToIntegerAndValidate({
  maxValue,
  name,
  nodeIndex,
}: {
  maxValue?: number;
  name: string;
  nodeIndex: string;
}): number {
  const trimmedIndex = nodeIndex.trim();
  let errorMessage = `${name} '${trimmedIndex}' is not a positive int`;
  const hasMaxValue = typeof maxValue === 'number';
  if (hasMaxValue) {
    errorMessage += ` (max ${maxValue})`;
  }

  if (!trimmedIndex) {
    throw new Error(errorMessage);
  }

  let integer: number;

  try {
    integer = Number(trimmedIndex);
  } catch (error) {
    throw new Error(errorMessage);
  }

  if (!Number.isInteger(integer) || integer < 0 || hasMaxValue && integer > maxValue) {
    throw new Error(errorMessage);
  }

  return integer;
}

export function extractTopLevelNodeIndex(serializedPosition: string): number | undefined {
  const { nodeIndices } = parseSerializedPosition(serializedPosition);
  return nodeIndices.pop();
}

export function parseSerializedPosition(serializedPosition: string, options?: {
  offsetMaxValue?: number;
  pathIndexMaxValue?: number;
}): {
  offset?: number;
  nodeIndices: number[];
} {
  let cleanedInput = serializedPosition;
  if (Number.isInteger(cleanedInput)) {
    cleanedInput = cleanedInput.toString();
  } else if (typeof cleanedInput !== 'string') {
    throw new Error('input is not a string');
  }

  cleanedInput = cleanedInput.trim();
  if (!cleanedInput) {
    throw new Error('(trimmed) input string is empty');
  }

  if (cleanedInput.includes(',')) {
    throw new Error('input contains ",". Note: this function accepts a serialized position (e.g. "1/2:3"), not a serialized location (e.g. "1/2:3,1/2:5").');
  }

  let path: string;
  let offset: number | undefined;

  if (cleanedInput.includes(serializedPositionOffsetDelimiter)) {
    const pieces = cleanedInput.split(serializedPositionOffsetDelimiter);
    path = pieces[0];
    // `split` has `limit` parameter but it doesn't work as expected / satisfy our tests
    const trimmedOffset = pieces.slice(1).join(serializedPositionOffsetDelimiter).trim();
    if (trimmedOffset) {
      offset = convertStringIndexToIntegerAndValidate({
        maxValue: options?.offsetMaxValue,
        name: 'offset',
        nodeIndex: trimmedOffset,
      });
    }
  } else {
    path = cleanedInput;
  }

  const nodeIndices = path.split(serializedPositionPathSegmentDelimiter)
  .filter((pathNodeIndex) => pathNodeIndex.trim())
  .map((pathNodeIndex) => convertStringIndexToIntegerAndValidate({
    name: 'path segment',
    maxValue: options?.pathIndexMaxValue,
    nodeIndex: pathNodeIndex,
  }));

  return {
    offset,
    nodeIndices,
  };
}

export function splitSerializedRange(serialized: string): { end: string; start: string; } {
  const result = deserializeRegex.exec(serialized);
  if (!result) {
    throw new Error('Failed to parse serialized location');
  }
  return { start: result[1], end: result[2] };
}

export function unparseSerializedPosition(nodeIndices: number[], offset?: number): string {
  let result = '';
  if (nodeIndices.length) {
    result = nodeIndices.join(serializedPositionPathSegmentDelimiter);
  }
  if (typeof offset === 'number') {
    result = [result, offset].join(serializedPositionOffsetDelimiter);
  }
  return result;
}
